import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  FilterListOff as FilterListOffIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  fetchBacktests,
  deleteBacktest,
  setFilters,
  clearSuccessMessage,
  fetchBacktestSummary
} from './backtestsSlice';
import { backtestAPI } from '../../api/backtests';
import BacktestRows, { MAX_COMPARE } from './list/BacktestRows';
import HypotheticalLegend from './results/HypotheticalLegend';
import CompareBar from './list/CompareBar';
import { rowHeadline } from './results/headline';
import { useBacktestPolling } from './hooks/useBacktestPolling';
import Pagination from '../../components/Pagination';
import DWLFLoader from '../../components/DWLFLoader';
import EmptyState from '../../components/common/EmptyState';
import PageHeader from '../../components/PageHeader';

// Add pulse animation styles
const pulseKeyframes = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const BacktestsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Inject pulse animation CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = pulseKeyframes;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const {
    backtests,
    backtestsLoading,
    backtestsError,
    filters,
    hasMore,
    cursor,
    currentPage,
    pageHistory,
    isDeleting,
    successMessage,
    summary,
    summaryLoading
  } = useSelector((state) => state.backtests);

  // Local state
  const [localFilters, setLocalFilters] = useState({ ...filters, strategyId: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [backtestToDelete, setBacktestToDelete] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [performanceData, setPerformanceData] = useState({});
  const [compareIds, setCompareIds] = useState([]);
  const requestedRef = useRef(new Set()); // runs whose summary has been asked for
  
  const toggleCompare = (id) => setCompareIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : ids.length >= MAX_COMPARE ? ids : [...ids, id]));
  const compareRuns = compareIds.map((id) => backtests.find((bt) => bt.requestId === id)).filter(Boolean);

  // Check if any filters are active (include both Redux filters and local-only strategyId filter)
  const hasActiveFilters = filters.status || localFilters.strategyId;
  
  // Notification state for completed backtests
  const [completionSnackbar, setCompletionSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });


  // Callback handlers for backtest completion
  const handleBacktestComplete = useCallback((backtest) => {
    setCompletionSnackbar({
      open: true,
      message: `✅ Backtest "${backtest.strategyName}" completed successfully!`,
      severity: 'success'
    });
    
    // Clear performance data cache for this backtest so it gets refetched
    setPerformanceData(prev => {
      const updated = { ...prev };
      delete updated[backtest.requestId];
      return updated;
    });
    requestedRef.current.delete(backtest.requestId);
  }, []);

  const handleBacktestFailed = useCallback((backtest) => {
    setCompletionSnackbar({
      open: true,
      message: `❌ Backtest "${backtest.strategyName}" failed to complete`,
      severity: 'error'
    });
  }, []);

  // Initialize polling hook
  const pollingStatus = useBacktestPolling(backtests, filters, {
    onBacktestComplete: handleBacktestComplete,
    onBacktestFailed: handleBacktestFailed,
    baseInterval: 10000, // 10 seconds
    activeInterval: 5000, // 5 seconds for active backtests
  });

  useEffect(() => {
    dispatch(fetchBacktests(filters));
    dispatch(fetchBacktestSummary());
  }, [dispatch, filters]);

  // Each completed run's headline from the API's SUMMARY read (~2 KB), four at a time (DWLF-306).
  // Fetches outlive a re-run of this effect (the list array changes as pages load); only unmounting
  // stops their results landing, so a run is asked for once and its headline always arrives.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  useEffect(() => {
    const requested = requestedRef.current;
    const queue = backtests.filter((bt) => bt.status === 'completed' && !requested.has(bt.requestId));
    if (!queue.length) return;
    queue.forEach((bt) => requested.add(bt.requestId));
    const worker = async () => {
      while (queue.length) {
        const bt = queue.shift();
        let headline = null;
        try {
          const res = await backtestAPI.getBacktestSummaryResults(bt.requestId);
          headline = rowHeadline(res.data?.data?.results || res.data?.results);
        } catch (e) { headline = null; }
        if (mountedRef.current) setPerformanceData((prev) => ({ ...prev, [bt.requestId]: headline }));
        else requested.delete(bt.requestId);
      }
    };
    Array.from({ length: 4 }, worker);
  }, [backtests]);

  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      setTimeout(() => {
        dispatch(clearSuccessMessage());
        setSnackbarOpen(false);
      }, 4000);
    }
  }, [successMessage, dispatch]);

  // Accumulate strategies across all pages so the dropdown stays consistent
  const seenStrategiesRef = useRef(new Map());
  const uniqueStrategies = useMemo(() => {
    backtests.forEach((bt) => {
      if (bt.strategyId && !seenStrategiesRef.current.has(bt.strategyId)) {
        seenStrategiesRef.current.set(bt.strategyId, bt.strategyName || bt.strategyId);
      }
    });
    return Array.from(seenStrategiesRef.current.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [backtests]);

  // Client-side filtering by strategy (backend doesn't support strategyId filter)
  const filteredBacktests = useMemo(() => {
    if (!localFilters.strategyId) return backtests;
    return backtests.filter((bt) => bt.strategyId === localFilters.strategyId);
  }, [backtests, localFilters.strategyId]);

  const handleFilterChange = (field, value) => {
    setLocalFilters({ ...localFilters, [field]: value });
  };

  const handleApplyFilters = () => {
    // strategyId is client-side only, don't send to API
    const { strategyId, ...apiFilters } = localFilters;
    const appliedFilters = { ...apiFilters, cursor: null };
    dispatch(setFilters(appliedFilters));
    dispatch(fetchBacktests(appliedFilters));
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: '',
      limit: 20
    };
    setLocalFilters({ ...clearedFilters, strategyId: '' });
    seenStrategiesRef.current.clear();
    dispatch(setFilters(clearedFilters));
    dispatch(fetchBacktests(clearedFilters));
  };

  const handlePageChange = (newPage) => {
    if (backtestsLoading) return;
    
    if (newPage === 1) {
      // Go to first page
      dispatch(fetchBacktests({ ...filters }));
    } else if (newPage > currentPage) {
      // Go to next page
      if (!hasMore || !cursor) return;
      dispatch(fetchBacktests({ 
        ...filters, 
        cursor: cursor,
        pageDirection: 'next',
        currentKey: cursor
      }));
    } else if (newPage < currentPage) {
      // Go to previous page
      const previousPageKey = newPage === 1 ? null : pageHistory[newPage - 2];
      dispatch(fetchBacktests({ 
        ...filters, 
        cursor: previousPageKey,
        pageDirection: 'prev'
      }));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchBacktests(filters));
    dispatch(fetchBacktestSummary());
  };

  const handleCreateNew = () => {
    navigate('/backtests/new');
  };

  const handleViewResults = (requestId) => {
    navigate(`/backtests/${requestId}/results`);
  };

  const handleDeleteClick = (backtest) => {
    setBacktestToDelete(backtest);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (backtestToDelete) {
      dispatch(deleteBacktest(backtestToDelete.requestId));
      setDeleteDialogOpen(false);
      setBacktestToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBacktestToDelete(null);
  };




  // Use summary from API (all backtests) instead of calculating from current page
  const enhancedSummary = useMemo(() => {
    if (!summary) {
      return {
        totalBacktests: 0,
        completedBacktests: 0,
        averageReturn: null,
        bestStrategy: null
      };
    }

    return {
      totalBacktests: summary.totalRequests || 0,
      completedBacktests: summary.completedRequests || 0,
      averageReturn: summary.averageReturn ?? null,
      bestStrategy: summary.bestStrategy ? {
        name: summary.bestStrategy.name,
        return: summary.bestStrategy.return
      } : null
    };
  }, [summary]);



  if (backtestsLoading && !backtests.length) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <PageHeader title="Backtests" subtitle="Test your strategies against historical data" />
        <Box sx={{ mb: 4 }}>
          <DWLFLoader size="large" />
        </Box>
        <DWLFLoader size="large" />
      </Container>
    );
  }

  

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <PageHeader
          title="Backtests"
          subtitle="Test your strategies against historical data"
          actions={
            <>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={backtestsLoading}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.7rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  borderRadius: 0,
                  borderColor: 'var(--dwlf-ink-20)',
                  color: 'var(--dwlf-ink-60)',
                  px: 2,
                  '&:hover': {
                    borderColor: 'var(--dwlf-accent)',
                    color: 'var(--dwlf-accent)',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {pollingStatus.isPolling ? 'Auto-refresh' : 'Refresh'}
              </Button>
              
              {/* Polling status indicator */}
              {pollingStatus.isPolling && (
                <Chip
                  size="small"
                  icon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
                  label={`${pollingStatus.activeCount} active`}
                  sx={{ 
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: '0.65rem',
                    borderRadius: 0,
                    animation: 'pulse 2s infinite',
                    borderColor: 'var(--dwlf-accent)',
                    color: 'var(--dwlf-accent)',
                  }}
                  variant="outlined"
                />
              )}

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  borderRadius: 0,
                  backgroundColor: 'var(--dwlf-accent)',
                  color: 'var(--dwlf-bg)',
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: 'var(--dwlf-text)',
                  },
                }}
              >
                New Backtest
              </Button>
            </>
          }
        />

        {/* Summary Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 0.75
          }}
        >
          <Box>
            <Card sx={{ width: '100%', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AssessmentIcon color="primary" sx={{ mr: 2 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Backtests
                    </Typography>
                    <Typography variant="h5">
                      {enhancedSummary.totalBacktests}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card sx={{ width: '100%', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUpIcon color="success" sx={{ mr: 2 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Completed
                    </Typography>
                    <Typography variant="h5">
                      {enhancedSummary.completedBacktests}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card sx={{ width: '100%', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AssessmentIcon color="info" sx={{ mr: 2 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Avg. Return
                    </Typography>
                    <Typography 
                      variant="h5"
                      color={enhancedSummary.averageReturn && enhancedSummary.averageReturn >= 0 ? 'success.main' : 'error.main'}
                    >
                      {enhancedSummary.averageReturn !== null ? 
                        `${enhancedSummary.averageReturn > 0 ? '+' : ''}${enhancedSummary.averageReturn.toFixed(1)}%` : 
                        'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box>
            <Card sx={{ width: '100%', height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingUpIcon color="warning" sx={{ mr: 2 }} />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Best Strategy
                    </Typography>
                    <Typography variant="h6" noWrap>
                      {enhancedSummary.bestStrategy?.name || 'N/A'}
                    </Typography>
                    {enhancedSummary.bestStrategy && (
                      <Typography variant="caption" color="success.main">
                        +{enhancedSummary.bestStrategy.return.toFixed(1)}%
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <HypotheticalLegend sx={{ mb: 4 }} />

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 200px 200px' },
              gap: 2,
              alignItems: 'end'
            }}
          >
            <FormControl fullWidth sx={{ minWidth: 0 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={localFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ minWidth: 0 }}>
              <InputLabel>Strategy</InputLabel>
              <Select
                value={localFilters.strategyId}
                onChange={(e) => handleFilterChange('strategyId', e.target.value)}
                label="Strategy"
              >
                <MenuItem value="">All Strategies</MenuItem>
                {uniqueStrategies.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ minWidth: 0 }}>
              <InputLabel>Results per page</InputLabel>
              <Select
                value={localFilters.limit}
                onChange={(e) => handleFilterChange('limit', e.target.value)}
                label="Results per page"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleApplyFilters} sx={{ width: '100%' }}>Apply Filters</Button>
            <Button variant="outlined" onClick={handleClearFilters} sx={{ width: '100%' }}>Clear Filters</Button>
          </Box>
        </Paper>

        {/* Error Alert */}
        {backtestsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {backtestsError}
          </Alert>
        )}

        {/* Loading Progress */}
        {backtestsLoading && (
          <Box sx={{ mb: 2 }}>
            <DWLFLoader size="small" showText={false} />
          </Box>
        )}

        {/* Backtests Table - render empty state OR table based on data */}
        {backtests.length === 0 && !backtestsLoading ? (
          hasActiveFilters ? (
            <EmptyState
              icon={<FilterListOffIcon sx={{ fontSize: 64 }} />}
              title="No results match your filters"
              subtitle="Try adjusting your filters or clear them to see all backtests."
              buttonLabel="Clear Filters"
              onAction={handleClearFilters}
            />
          ) : (
            // Empty state when no backtests exist at all
            <EmptyState
              icon={<AssessmentIcon sx={{ fontSize: 64 }} />}
              title="No backtests yet"
              subtitle="Create your first strategy and run a backtest to see how it performs."
              buttonLabel="Create Backtest"
              onAction={handleCreateNew}
              showAddIcon
            />
          )
        ) : filteredBacktests.length === 0 && hasActiveFilters && !backtestsLoading ? (
          <EmptyState
            icon={<FilterListOffIcon sx={{ fontSize: 64 }} />}
            title="No results match your filters"
            subtitle="Try adjusting your filters or clear them to see all backtests."
            buttonLabel="Clear Filters"
            onAction={handleClearFilters}
          />
        ) : (
          <>
            <BacktestRows
              backtests={filteredBacktests}
              headlines={performanceData}
              selected={compareIds}
              onToggle={toggleCompare}
              onView={handleViewResults}
              onDelete={handleDeleteClick}
            />
            <HypotheticalLegend sx={{ mt: 0.75 }} />
            {filteredBacktests.length > 0 && (
              <Pagination
                currentPage={currentPage}
                hasMore={hasMore}
                onPageChange={handlePageChange}
                loading={backtestsLoading}
                itemsPerPage={filters.limit}
                totalItemsOnPage={filteredBacktests.length}
              />
            )}
            {compareRuns.length >= 2 && <CompareBar runs={compareRuns} onClear={() => setCompareIds([])} />}
            {compareRuns.length === 1 && (
              <Typography sx={{ mt: 1, fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.75rem', color: 'var(--dwlf-ink-55)' }}>
                Tick one or two more completed runs to compare their equity curves.
              </Typography>
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Backtest</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the backtest for strategy "{backtestToDelete?.strategyName}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Backtest Completion Snackbar */}
        <Snackbar
          open={completionSnackbar.open}
          autoHideDuration={6000}
          onClose={() => setCompletionSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            severity={completionSnackbar.severity} 
            onClose={() => setCompletionSnackbar(prev => ({ ...prev, open: false }))}
            variant="filled"
            sx={{ minWidth: '300px' }}
          >
            {completionSnackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default BacktestsPage; 
